'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { seafarerProfileDb } from '@/lib/db';
import { useAuth } from '@/contexts/AuthContext';
import { safeStorage } from '@/lib/safe-storage';
import { createClient } from '@/lib/supabase/client';
import type { FullSeafarerData } from '@/lib/types/seafarer';

type SeafarerProfileContextType = {
  data: FullSeafarerData | null;
  isLoading: boolean;
  cloudSyncStatus: 'idle' | 'syncing' | 'synced' | 'pending' | 'error';
  lastCloudSyncedAt: string | null;
  saveData: (data: FullSeafarerData) => Promise<void>;
  clearData: () => Promise<void>;
  retryCloudSync: () => Promise<void>;
};

type CloudProfile = {
  data: FullSeafarerData;
  updatedAt: string | null;
};

const SeafarerProfileContext = createContext<SeafarerProfileContextType>({
  data: null,
  isLoading: true,
  cloudSyncStatus: 'idle',
  lastCloudSyncedAt: null,
  saveData: async () => {},
  clearData: async () => {},
  retryCloudSync: async () => {},
});

export const useSeafarerProfile = () => useContext(SeafarerProfileContext);

async function fetchCloudProfile(userId?: string | null): Promise<CloudProfile | null> {
  if (!userId) return null;
  const supabase = createClient();
  if (!supabase) return null;
  const { data, error } = await (supabase as any)
    .from('seafarer_profiles')
    .select('data, updated_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return null;
  if (!data?.data) return null;
  return {
    data: data.data as FullSeafarerData,
    updatedAt: data.updated_at ?? null,
  };
}

async function saveCloudProfile(userId: string | undefined | null, data: FullSeafarerData): Promise<void> {
  if (!userId) return;
  const supabase = createClient();
  if (!supabase) return;
  const { error } = await (supabase as any)
    .from('seafarer_profiles')
    .upsert({ user_id: userId, data, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
  if (error) throw error;
}

async function clearCloudProfile(userId?: string | null): Promise<void> {
  if (!userId) return;
  const supabase = createClient();
  if (!supabase) return;
  const { error } = await (supabase as any).from('seafarer_profiles').delete().eq('user_id', userId);
  if (error) throw error;
}

const PROFILE_PENDING_SYNC_KEY = 'crewnest_profile_pending_cloud_sync';
const PROFILE_LAST_CLOUD_SYNC_KEY = 'crewnest_profile_last_cloud_sync';
const PROFILE_LOCAL_UPDATED_KEY = 'crewnest_profile_local_updated_at';

function isBrowserOnline() {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

export function SeafarerProfileProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading, isConfigured } = useAuth();
  const [data, setData] = useState<FullSeafarerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cloudSyncStatus, setCloudSyncStatus] = useState<SeafarerProfileContextType['cloudSyncStatus']>('idle');
  const [lastCloudSyncedAt, setLastCloudSyncedAt] = useState<string | null>(null);

  useEffect(() => {
    setLastCloudSyncedAt(safeStorage.get(PROFILE_LAST_CLOUD_SYNC_KEY));
    if (safeStorage.get(PROFILE_PENDING_SYNC_KEY) === 'true') {
      setCloudSyncStatus('pending');
    }
  }, []);

  const markCloudSynced = useCallback(() => {
    const now = new Date().toISOString();
    safeStorage.set(PROFILE_LAST_CLOUD_SYNC_KEY, now);
    safeStorage.remove(PROFILE_PENDING_SYNC_KEY);
    safeStorage.remove(PROFILE_LOCAL_UPDATED_KEY);
    setLastCloudSyncedAt(now);
    setCloudSyncStatus('synced');
  }, []);

  const markCloudPending = useCallback(() => {
    safeStorage.set(PROFILE_PENDING_SYNC_KEY, 'true');
    setCloudSyncStatus('pending');
  }, []);

  useEffect(() => {
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (isConfigured && !user) {
      setData(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const stored = await seafarerProfileDb.get();
        if (cancelled) return;

        if (isConfigured && user?.id) {
          if (!isBrowserOnline()) {
            setData(stored);
            return;
          }

          const hasPendingLocalSync = safeStorage.get(PROFILE_PENDING_SYNC_KEY) === 'true';
          const localUpdatedAt = safeStorage.get(PROFILE_LOCAL_UPDATED_KEY);
          const cloud = await fetchCloudProfile(user.id);

          if (
            hasPendingLocalSync &&
            stored &&
            localUpdatedAt &&
            (!cloud?.updatedAt || new Date(localUpdatedAt).getTime() > new Date(cloud.updatedAt).getTime())
          ) {
            await saveCloudProfile(user.id, stored);
            if (cancelled) return;
            markCloudSynced();
            setData(stored);
            return;
          }

          if (cloud) {
            await seafarerProfileDb.save(cloud.data);
            if (cancelled) return;
            markCloudSynced();
            setData(cloud.data);
            return;
          }

          setData(stored);
          return;
        }

        setData(stored);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isConfigured, markCloudPending, markCloudSynced, user?.id]);

  const saveData = useCallback(async (incoming: FullSeafarerData) => {
    await seafarerProfileDb.save(incoming);
    setData(incoming);
    safeStorage.set(PROFILE_LOCAL_UPDATED_KEY, new Date().toISOString());
    // Invalidate cert version so CertificatesContext rebuilds from updated profile on next load
    safeStorage.remove('crewnest_cert_version');

    if (isConfigured && user?.id) {
      if (!isBrowserOnline()) {
        markCloudPending();
        return;
      }

      setCloudSyncStatus('syncing');
      try {
        await saveCloudProfile(user.id, incoming);
        markCloudSynced();
      } catch (error) {
        console.error('Failed to sync seafarer profile:', error);
        markCloudPending();
        throw error;
      }
    }
  }, [isConfigured, markCloudPending, markCloudSynced, user?.id]);

  const clearData = useCallback(async () => {
    await seafarerProfileDb.clear();
    if (isConfigured && user?.id) {
      if (isBrowserOnline()) {
        try {
          await clearCloudProfile(user.id);
          markCloudSynced();
        } catch {
          markCloudPending();
        }
      } else {
        markCloudPending();
      }
    }
    setData(null);
  }, [isConfigured, markCloudPending, markCloudSynced, user?.id]);

  const retryCloudSync = useCallback(async () => {
    if (!isConfigured || !user?.id || !isBrowserOnline()) return;
    const stored = await seafarerProfileDb.get();
    if (!stored) return;

    setCloudSyncStatus('syncing');
    try {
      await saveCloudProfile(user.id, stored);
      markCloudSynced();
    } catch {
      markCloudPending();
    }
  }, [isConfigured, markCloudPending, markCloudSynced, user?.id]);

  useEffect(() => {
    const onOnline = () => {
      if (safeStorage.get(PROFILE_PENDING_SYNC_KEY) === 'true') {
        retryCloudSync().catch(() => markCloudPending());
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [markCloudPending, retryCloudSync]);

  const value = useMemo(
    () => ({ data, isLoading, cloudSyncStatus, lastCloudSyncedAt, saveData, clearData, retryCloudSync }),
    [data, isLoading, cloudSyncStatus, lastCloudSyncedAt, saveData, clearData, retryCloudSync]
  );

  return (
    <SeafarerProfileContext.Provider value={value}>
      {children}
    </SeafarerProfileContext.Provider>
  );
}
